/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import StatusChip from 'components/@extended/StatusChip';
import AuditInfo from 'components/audit/AuditInfo';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import Loader from 'components/Loader';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import { format } from 'date-fns';
import { defaultCurrencyOption, useDefaultCurrencyCode } from 'hooks/useDefaultCurrencyCode';
import { getInvoiceStatusChipType } from 'pages/invoices/util/invoiceUtil';
import TimecardLanguageLabel from 'pages/timecards/labels/TimecardLanguageLabel.tsx';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useGetMattersListQuery, useLazyGetMattersListQuery } from 'store/api/matters';
import { ExpenseCreateDto, ExpenseForm } from 'types/expenses';
import { FeatureFlag } from 'types/features';
import { TenantDto } from 'types/tenants';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getLabelWithDescription, getValidOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations';
import { renderMatterIndicators, renderMatterOptionWithRestrictedSelection } from 'utils/matterOptionRenderer';
import { partyNumberString } from 'utils/partyNameUtil';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate.ts';
import { useClassifiers } from 'utils/useClassifiers.ts';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import InputWrapper from '../../forms/inputs/rhf-inputs/InputWrapper.tsx';
import { openCasesFilter } from '../../tables/list-table/filter-pieces/filterConstants.ts';
import { ModalFooter } from '../ModalFooter';
import { ModalHeader } from '../ModalHeader';
import { sortCurrencies } from './sortCurrencies.ts';

interface Props {
    titleId?: string;
    onChange?: (value: ExpenseCreateDto) => void;
    onChangeRaw?: (value: ExpenseForm) => void;
    onClose: () => void;
    initialData?: ExpenseForm;
    disableSelectMatter?: boolean;
    hideSelectMatter?: boolean;
    tenant?: TenantDto;
    readOnly: boolean;
    preselectMatterId?: number;
    forceNonBillable?: boolean;
    isNewInvoiceExpense?: boolean;
}

const defaultValues: ExpenseForm = {
    externalId: null,
    expenseType: null,
    currencyType: null,
    description: '',
    expenseAmount: '',
    expenseDate: format(new Date(), DEFAULT_DATE_FORMAT),
    isNonBillable: false,
    employee: null,
    matter: null,
    timecardId: null,
    effortId: null,
};

const USED_CLASSIFIERS = ['CURRENCY', 'EXPENSE_TYPE', 'LANGUAGE', 'INVOICE_STATUS_TYPE'];

const ExpenseModal = (props: Props) => {
    const isNegativeAmountAllowed = useIsFeatureEnabled(FeatureFlag.EXPENSES_NEGATIVE_AMOUNT_ALLOWED);
    const isPartyNumberUsageEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_NUMBER_USAGE);
    const {
        titleId = 'expenses.edit',
        initialData,
        disableSelectMatter,
        hideSelectMatter,
        onClose,
        onChange,
        onChangeRaw,
        readOnly,
        tenant,
        preselectMatterId,
        forceNonBillable,
        isNewInvoiceExpense,
    } = props;

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: USED_CLASSIFIERS,
    });

    const { code: defaultCurrencyCode, isLoading: isLoadingDefaultCurrency } = useDefaultCurrencyCode();

    const [initialModalData, setInitialModalData] = useState<ExpenseForm | undefined>();

    const { data: preselectedMatterQueryData } = useGetMattersListQuery(
        { pageSize: 1, page: 0, filters: `id==${preselectMatterId}` },
        { skip: preselectMatterId === undefined }
    );

    const preselectedMatterData = useMemo(() => {
        return preselectedMatterQueryData?.content.at(0);
    }, [preselectedMatterQueryData]);

    const { handleSubmit, control, setValue, getValues, watch } = useForm<ExpenseForm>({
        defaultValues: defaultValues,
        values: initialModalData,
    });
    const currentMatter = watch('matter');

    const { data: currentMatterQueryData } = useGetMattersListQuery(
        { pageSize: 1, page: 0, filters: `id==${currentMatter?.id}` },
        { skip: !currentMatter?.id }
    );

    const currentMatterData = useMemo(() => {
        if (!currentMatter) {
            return null;
        }
        return currentMatterQueryData?.content.at(0);
    }, [currentMatterQueryData, currentMatter]);

    const handleOnChange = (data: ExpenseForm) => {
        if (onChange) onChange(mapFormDataToDto(data));
        if (onChangeRaw) onChangeRaw(data);
        onClose();
    };
    const onSave = async () => {
        await handleSubmit((data) => handleOnChange(data))();
    };

    useEffect(() => {
        if (isLoadingClassifiers || isLoadingDefaultCurrency) {
            return;
        }
        // A new expense starts in the firm's default currency; an edit and a duplicate keep the one they carry.
        const startData =
            initialData && !initialData.id && !initialData.currencyType
                ? {
                      ...initialData,
                      currencyType: defaultCurrencyOption(
                          getValidOptionsFromClassifier<string, string>(classifiers['CURRENCY']),
                          defaultCurrencyCode
                      ),
                  }
                : initialData;
        if (preselectMatterId === undefined) {
            setInitialModalData(startData);
            return;
        }
        if (preselectedMatterData) {
            setInitialModalData({
                ...startData,
                matter: {
                    id: preselectedMatterData.id,
                    name: preselectedMatterData.name,
                    fullName: preselectedMatterData.fullName,
                    isNonBillable: preselectedMatterData.isNonBillable || forceNonBillable,
                    isConfidential: preselectedMatterData.isConfidential,
                    visibility: preselectedMatterData.visibility,
                },
            } as ExpenseForm);
        }
    }, [
        preselectedMatterData,
        preselectMatterId,
        initialData,
        forceNonBillable,
        classifiers,
        isLoadingClassifiers,
        defaultCurrencyCode,
        isLoadingDefaultCurrency,
    ]);

    // On edit the checkbox must show the stored value, not the matter-derived one: expenses that
    // predate their case turning non-billable still hold false, and auto-checking would misreport
    // the row. Auto-check only for new expenses or when the user switches the case to a non-billable
    // one, where the checked box previews what the save will store.
    useEffect(() => {
        const isEditingStoredValue = !!initialModalData?.id && currentMatter?.id === initialModalData?.matter?.id;
        if (currentMatter?.isNonBillable && !isEditingStoredValue) {
            setValue('isNonBillable', true);
        } else if (isEditingStoredValue) {
            setValue('isNonBillable', initialModalData?.isNonBillable ?? false);
        }
    }, [currentMatter, setValue, initialModalData]);

    if (!initialModalData || isLoadingClassifiers) {
        return <Loader />;
    }

    const matter = currentMatterData?.id === getValues('matter')?.id ? currentMatterData : null;
    // Same rule for the dropdown's group heading and the client line under the field, so they stay in step.
    const selectedClientParty = matter?.clientParty;
    const selectedClientName = selectedClientParty
        ? [partyNumberString(selectedClientParty, isPartyNumberUsageEnabled), selectedClientParty.name]
              .filter(Boolean)
              .join(' ')
        : null;
    const customCurrencyPriority = [matter?.pricelistCurrencyTypeCode, tenant?.baseCurrencyTypeCode].filter(
        (code): code is string => typeof code === 'string'
    );
    const currencyOptions = (
        (getValidOptionsFromClassifier(classifiers['CURRENCY']) ?? []) as {
            label: string;
            value: string;
        }[]
    ).sort((a, b) => sortCurrencies(a, b, customCurrencyPriority));

    const expenseTypeOptions = getValidOptionsFromClassifier(classifiers['EXPENSE_TYPE']) ?? [];

    return (
        <>
            <ModalHeader title={readOnly ? t('expenses.view-expense') : t(titleId)} onCancel={onClose} />
            <div className="h-full overflow-y-auto px-8 py-[38px] max-sm:px-4 max-sm:py-6">
                <div className="flex flex-col gap-4">
                    {initialData?.id ? (
                        <InputWrapper label={'expenses.id'} name={'expense.id'} hasErrors={false} readonly={true}>
                            <>{initialData?.id}</>
                        </InputWrapper>
                    ) : null}
                    {initialData?.externalId ? (
                        <InputWrapper
                            label={'expenses.external-id'}
                            name={'expense.external-id'}
                            hasErrors={false}
                            readonly={true}
                        >
                            <>{initialData?.externalId}</>
                        </InputWrapper>
                    ) : null}
                    {!hideSelectMatter &&
                    (disableSelectMatter || readOnly || isNewInvoiceExpense) &&
                    preselectedMatterData ? (
                        <>
                            <InputWrapper
                                label={'expenses.form.client'}
                                name={'expense.client'}
                                hasErrors={false}
                                readonly={true}
                            >
                                <PartyName
                                    party={preselectedMatterData.clientParty}
                                    isLink
                                    useLinkWithOrigin
                                    linkTarget="_blank"
                                    variant="readonly-other"
                                />
                            </InputWrapper>
                            <InputWrapper
                                label={'expenses.form.case'}
                                name={'expense.case'}
                                hasErrors={false}
                                readonly={true}
                            >
                                <RecordLink
                                    withOrigin
                                    to={`/matter/${preselectedMatterData.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-link"
                                    label={preselectedMatterData.fullName}
                                />
                            </InputWrapper>
                        </>
                    ) : !hideSelectMatter ? (
                        <div>
                            <DynamicSelectInput
                                setValue={setValue}
                                control={control}
                                label="expenses.form.case-client"
                                required
                                name="matter"
                                paginated={{
                                    fetch: useLazyGetMattersListQuery,
                                    filters: openCasesFilter,
                                    searchField: ['fullName', 'clientParty.name'],
                                    sort: ['clientParty.name,asc', 'name,asc'],
                                }}
                                groupByField="clientParty.name"
                                getOptionLabel="fullName"
                                getOptionIdentifier="id"
                                placeholder="expenses.form.case-client"
                                readonly={false}
                                getSelectedValueStartAdornment={renderMatterIndicators}
                                customOptionRenderer={(option, props, getValue) =>
                                    renderMatterOptionWithRestrictedSelection(
                                        option,
                                        props,
                                        getValue,
                                        true,
                                        'dashboard.tooltips.expense-blocked'
                                    )
                                }
                                getGroupStartAdornment={
                                    isPartyNumberUsageEnabled
                                        ? (_group, options) => {
                                              const groupClient = options?.[0]?.clientParty;
                                              return groupClient
                                                  ? partyNumberString(groupClient, isPartyNumberUsageEnabled)
                                                  : null;
                                          }
                                        : undefined
                                }
                            />
                            {selectedClientName && (
                                <div className="text-fg pt-1 text-sm">
                                    {t('expenses.form.client')}: {selectedClientName}
                                </div>
                            )}
                        </div>
                    ) : null}

                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="expenses.form.expense-type"
                        required
                        name="expenseType"
                        options={expenseTypeOptions}
                        getOptionLabel={getLabelWithDescription(classifiers['EXPENSE_TYPE'])}
                        getOptionIdentifier={(option) => String(option.value)}
                        placeholder="expenses.form.expense-type"
                        readonly={readOnly}
                    />

                    <div>
                        <div className="flex flex-row gap-4">
                            <div className="min-w-0 flex-1">
                                <DecimalInput
                                    name="expenseAmount"
                                    control={control}
                                    label="expenses.form.amount"
                                    placeholder="expenses.form.amount"
                                    required
                                    readonly={readOnly}
                                    allowNegative={isNegativeAmountAllowed}
                                />
                            </div>
                            <div className="min-w-0 flex-1">
                                <SelectInput
                                    setValue={setValue}
                                    control={control}
                                    label="expenses.form.currency"
                                    required
                                    name="currencyType"
                                    options={currencyOptions}
                                    getOptionLabel="label"
                                    getOptionIdentifier="value"
                                    placeholder="expenses.form.currency"
                                    readonly={readOnly}
                                />
                            </div>
                        </div>
                    </div>

                    <DynamicSelectInput
                        setValue={setValue}
                        control={control}
                        label="expenses.form.added-by"
                        required
                        name="employee"
                        paginated={{
                            fetch: useLazyGetEmployeesListWithFiltersQuery,
                            searchField: 'fullName',
                        }}
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                        placeholder="expenses.form.added-by"
                        readonly={readOnly}
                    />
                    <div>
                        <div className="flex flex-row gap-4">
                            <div className="min-w-0 flex-1">
                                <DateInput
                                    control={control}
                                    name="expenseDate"
                                    label="date"
                                    required
                                    readonly={readOnly}
                                    maxDate={new Date()}
                                />
                            </div>
                        </div>
                    </div>
                    <TextAreaInput
                        name="description"
                        control={control}
                        label="expenses.form.details-of-expense"
                        placeholder="expenses.form.details-of-expense"
                        readonly={readOnly}
                        rightLabelComponent={
                            currentMatterData?.timecardLanguageCode ? (
                                <div>
                                    <TimecardLanguageLabel
                                        classifierData={classifiers?.['LANGUAGE']}
                                        timecardLanguageTypeCode={currentMatterData?.timecardLanguageCode}
                                    />
                                </div>
                            ) : null
                        }
                    />
                    {isNewInvoiceExpense ? null : (
                        <CheckboxInput
                            control={control}
                            name="isNonBillable"
                            label="expenses.form.is-non-billable"
                            readonly={readOnly}
                            disabled={currentMatter?.isNonBillable || forceNonBillable}
                        />
                    )}

                    {initialData?.invoices && initialData.invoices.length > 0 ? (
                        <InputWrapper
                            label={'expenses.connected-invoices'}
                            name={'expense.connected-invoices'}
                            hasErrors={false}
                            readonly={true}
                        >
                            <div className="flex flex-col gap-1">
                                {initialData.invoices.map((invoice) => (
                                    <div key={invoice.id} className="grid grid-cols-[auto_auto_1fr] items-center gap-2">
                                        <RecordLink
                                            withOrigin
                                            to={`/invoice/${invoice.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-link"
                                            label={invoice.invoiceNumber}
                                        />
                                        <div className="whitespace-nowrap">
                                            {formatDateStringInput(new Date(invoice.invoiceDate))}
                                        </div>
                                        <div className="justify-self-end">
                                            <StatusChip
                                                type={getInvoiceStatusChipType(invoice.invoiceStatusTypeCode)}
                                                label={getClassifierText(
                                                    classifiers['INVOICE_STATUS_TYPE'],
                                                    invoice.invoiceStatusTypeCode
                                                )}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </InputWrapper>
                    ) : null}

                    {initialData?.id && initialData.createdAt ? (
                        <div className="mt-4">
                            <AuditInfo
                                createdAt={initialData.createdAt}
                                createdByEmployee={initialData.createdByEmployee}
                                lastModifiedAt={initialData.lastModifiedAt}
                                changedByEmployee={initialData.changedByEmployee}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
            <ModalFooter
                onCancel={onClose}
                onSave={() => {
                    void onSave();
                }}
                readOnly={readOnly}
            />
        </>
    );
};

export default ExpenseModal;

export const mapFormDataToDto = (expense: ExpenseForm): ExpenseCreateDto => ({
    expenseTypeCode: expense?.expenseType?.value ?? null,
    originalCurrencyTypeCode: expense?.currencyType?.value ?? null,
    description: expense?.description ?? '',
    expenseAmountOriginalCurrency: expense?.expenseAmount ?? null,
    expenseDate: expense?.expenseDate ? new Date(expense.expenseDate) : null,
    expenseDateUtcTimestamp: expense?.expenseDate ? new Date(`${expense.expenseDate}T00:00:00`).toISOString() : '',
    isNonBillable: expense?.isNonBillable ?? false,
    employeeId: expense?.employee?.id ?? null,
    matterId: expense?.matter?.id ?? null,
    timecardId: expense?.timecardId ?? null,
    effortId: expense?.effortId ?? null,
});
